/**
 * LocationPermissionDialog Component
 * Custom location permission prompt with privacy-first duration options:
 * - 24 Hours
 * - 12 Hours
 * - While Using App (session)
 * - Just Once
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import BlockIcon from '@mui/icons-material/Block';
import { telegram } from '../services/telegram';
import { useTranslation } from '../i18n/translations';
import { requestPreciseLocation } from '../services/highPrecisionLocation';

// Storage key for permission preference
const LOCATION_PERMISSION_KEY = 'ziawellnav_location_permission';
const LAST_CHOICE_KEY = 'ziawellnav_location_last_choice';

// Duration options in milliseconds
const DURATION_MS = {
  '24h': 24 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
  'session': 4 * 60 * 60 * 1000, // 4 hours for session
  'once': 0 // No expiry, cleared after single use
};

export type LocationPermissionChoice = 
  | '24h'            // Allow for 24 hours
  | '12h'            // Allow for 12 hours
  | 'session'        // Allow while using app (4 hours)
  | 'once'           // Allow this one time
  | 'ask';           // Default - ask each time

interface StoredPermission {
  choice: LocationPermissionChoice;
  timestamp: number;
  expiry?: number; // When this permission expires
}

interface LocationPermissionDialogProps {
  open: boolean;
  onClose: () => void;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
  featureName?: string; // e.g., "Navigate to nearest well", "Share location with crew"
}

/**
 * Get stored permission preference
 */
export function getStoredPermission(): StoredPermission | null {
  try {
    const stored = localStorage.getItem(LOCATION_PERMISSION_KEY);
    if (!stored) return null;
    
    const permission: StoredPermission = JSON.parse(stored);
    
    // Check if session permission has expired
    if (permission.choice === 'session' && permission.expiry) {
      if (Date.now() > permission.expiry) {
        // Session expired, clear it
        localStorage.removeItem(LOCATION_PERMISSION_KEY);
        return null;
      }
    }
    
    // "Once" permission is only valid for a single use - clear after reading
    if (permission.choice === 'once') {
      localStorage.removeItem(LOCATION_PERMISSION_KEY);
    }
    
    return permission;
  } catch {
    return null;
  }
}

/**
 * Get the last choice the user made (for pre-selection)
 */
export function getLastChoice(): LocationPermissionChoice {
  try {
    const stored = localStorage.getItem(LAST_CHOICE_KEY);
    if (stored) {
      return stored as LocationPermissionChoice;
    }
  } catch {}
  return 'session'; // Default to "While Using App"
}

/**
 * Save permission preference with expiry
 */
export function savePermission(choice: LocationPermissionChoice): void {
  const permission: StoredPermission = {
    choice,
    timestamp: Date.now(),
  };
  
  // Set expiry based on choice (not for 'once' or 'ask')
  if (choice !== 'once' && choice !== 'ask' && choice in DURATION_MS) {
    permission.expiry = Date.now() + DURATION_MS[choice as keyof typeof DURATION_MS];
  }
  
  localStorage.setItem(LOCATION_PERMISSION_KEY, JSON.stringify(permission));
  
  // Remember this choice for next time (except 'once' and 'ask')
  if (choice !== 'once' && choice !== 'ask') {
    localStorage.setItem(LAST_CHOICE_KEY, choice);
  }
}

/**
 * Get remaining time on current permission
 */
export function getPermissionTimeRemaining(): { 
  hasPermission: boolean; 
  minutesRemaining: number | null;
  choice: LocationPermissionChoice | null;
} {
  const stored = getStoredPermission();
  
  if (!stored || stored.choice === 'once') {
    return { hasPermission: false, minutesRemaining: null, choice: null };
  }
  
  if (stored.expiry) {
    const remaining = stored.expiry - Date.now();
    if (remaining <= 0) {
      return { hasPermission: false, minutesRemaining: null, choice: null };
    }
    return { 
      hasPermission: true, 
      minutesRemaining: Math.ceil(remaining / (60 * 1000)),
      choice: stored.choice
    };
  }
  
  return { hasPermission: true, minutesRemaining: null, choice: stored.choice };
}

/**
 * Clear stored permission (useful for settings)
 */
export function clearStoredPermission(): void {
  localStorage.removeItem(LOCATION_PERMISSION_KEY);
}

/**
 * Check if we should show the permission dialog
 * Returns true if we need to ask, false if we have valid permission
 */
export function shouldAskPermission(): { shouldAsk: boolean; storedChoice?: LocationPermissionChoice } {
  const stored = getStoredPermission();
  
  if (!stored) {
    return { shouldAsk: true };
  }
  
  // Check if permission has expired
  if (stored.expiry && Date.now() > stored.expiry) {
    clearStoredPermission();
    return { shouldAsk: true };
  }
  
  // For timed permissions (24h, 12h, session), check validity
  if (stored.choice === '24h' || stored.choice === '12h' || stored.choice === 'session') {
    return { shouldAsk: false, storedChoice: stored.choice };
  }
  
  // "once" was already used and cleared in getStoredPermission
  return { shouldAsk: true };
}

/**
 * Hook to manage location permission flow
 */
export function useLocationPermission() {
  const [showDialog, setShowDialog] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<{
    onGranted: () => void;
    onDenied: () => void;
    featureName?: string;
  } | null>(null);
  const [featureName, setFeatureName] = useState<string>('');

  const requestPermission = useCallback((
    onGranted: () => void,
    onDenied: () => void,
    feature?: string
  ) => {
    const { shouldAsk, storedChoice } = shouldAskPermission();
    
    if (!shouldAsk && storedChoice) {
      // We have valid permission
      onGranted();
      return;
    }
    
    // Need to show dialog
    setFeatureName(feature || 'access your location');
    setPendingCallback({ onGranted, onDenied, featureName: feature });
    setShowDialog(true);
  }, []);

  const handlePermissionGranted = useCallback(() => {
    setShowDialog(false);
    pendingCallback?.onGranted();
    setPendingCallback(null);
  }, [pendingCallback]);

  const handlePermissionDenied = useCallback(() => {
    setShowDialog(false);
    pendingCallback?.onDenied();
    setPendingCallback(null);
  }, [pendingCallback]);

  const handleClose = useCallback(() => {
    setShowDialog(false);
    // Treat close as "ask again next time"
    setPendingCallback(null);
  }, []);

  return {
    showDialog,
    featureName,
    requestPermission,
    handlePermissionGranted,
    handlePermissionDenied,
    handleClose,
  };
}

export function LocationPermissionDialog({
  open,
  onClose,
  onPermissionGranted,
  onPermissionDenied,
  featureName: _featureName = 'access your location'
}: LocationPermissionDialogProps) {
  const { t } = useTranslation();
  const [selectedChoice, setSelectedChoice] = useState<LocationPermissionChoice>('session');
  const [isRequesting, setIsRequesting] = useState(false);

  const handleAllow = async () => {
    setIsRequesting(true);
    telegram.hapticFeedback('light');
    
    // Save the permission choice
    savePermission(selectedChoice);
    
    // Now request actual browser/Telegram location permission
    try {
      // Try Telegram location first
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.requestLocation) {
        tg.requestLocation((success: boolean) => {
          setIsRequesting(false);
          if (success) {
            telegram.hapticFeedback('success');
            onPermissionGranted();
          } else {
            // User denied in Telegram's native prompt - clear our saved permission
            clearStoredPermission();
            telegram.hapticFeedback('error');
            onPermissionDenied();
          }
        });
        return;
      }
      
      const result = await requestPreciseLocation({
        desiredAccuracyMeters: 50,
        refinementTimeoutMs: 7000,
        allowLastKnownFallback: false,
        allowTelegramFallback: false,
      });

      setIsRequesting(false);

      if (result.fix) {
        telegram.hapticFeedback('success');
        onPermissionGranted();
        return;
      }

      if (result.reason && result.reason.toLowerCase().includes('denied')) {
        clearStoredPermission();
      }
      telegram.hapticFeedback('error');
      onPermissionDenied();
      return;
    } catch (error) {
      setIsRequesting(false);
      telegram.hapticFeedback('error');
      onPermissionDenied();
    }
  };

  const handleDeny = () => {
    telegram.hapticFeedback('warning');
    // Just close without saving - user can try again later
    onPermissionDenied();
  };

  // Get last choice for pre-selection
  const lastChoice = getLastChoice();
  
  const permissionOptions = [
    {
      value: '24h' as LocationPermissionChoice,
      label: '24 Hours',
      description: 'Location access for the next 24 hours',
      icon: <TimerIcon sx={{ color: '#10b981' }} />,
      color: '#10b981',
    },
    {
      value: '12h' as LocationPermissionChoice,
      label: '12 Hours',
      description: 'Location access for the next 12 hours',
      icon: <TimerIcon sx={{ color: '#22c55e' }} />,
      color: '#22c55e',
    },
    {
      value: 'session' as LocationPermissionChoice,
      label: 'While Using App',
      description: 'Location access for this session (~4 hours)',
      icon: <AccessTimeIcon sx={{ color: '#3b82f6' }} />,
      color: '#3b82f6',
    },
    {
      value: 'once' as LocationPermissionChoice,
      label: 'Just Once',
      description: 'Allow location for this action only',
      icon: <LooksOneIcon sx={{ color: '#f59e0b' }} />,
      color: '#f59e0b',
    },
  ];

  // Pre-select last choice or default to 'session'
  useEffect(() => {
    if (open) {
      setSelectedChoice(lastChoice);
      setIsRequesting(false);
    }
  }, [open, lastChoice]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        pb: 2
      }}>
        <MyLocationIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('locationPermissionTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('locationPermissionDescription')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Alert 
          severity="info" 
          icon={<SecurityIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            Your location is used for navigation, finding nearby wells, and sharing with your crew. 
            We never store or share your location without permission.
          </Typography>
        </Alert>

        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedChoice}
            onChange={(e) => setSelectedChoice(e.target.value as LocationPermissionChoice)}
          >
            {permissionOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio 
                    sx={{ 
                      color: option.color,
                      '&.Mui-checked': { color: option.color }
                    }} 
                  />
                }
                label={
                  <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      <Typography sx={{ fontWeight: 500 }}>
                        {option.label}
                      </Typography>
                      {option.value === 'session' && (
                        <Chip 
                          label="Recommended" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(16, 185, 129, 0.2)', 
                            color: '#10b981',
                            fontSize: '0.7rem',
                            height: 20
                          }} 
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4 }}>
                      {option.description}
                    </Typography>
                  </Box>
                }
                sx={{
                  border: selectedChoice === option.value 
                    ? `2px solid ${option.color}` 
                    : '2px solid transparent',
                  borderRadius: 2,
                  mb: 1,
                  mx: 0,
                  p: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                  }
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        gap: 1
      }}>
        <Button
          onClick={handleDeny}
          startIcon={<BlockIcon />}
          sx={{ 
            color: '#ef4444',
            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
          }}
        >
          {t('locationPermissionNever')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
        >
          {t('locationPermissionLater')}
        </Button>
        <Button
          onClick={handleAllow}
          variant="contained"
          disabled={isRequesting}
          startIcon={<LocationOnIcon />}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            }
          }}
        >
          {isRequesting ? t('loading') : t('locationPermissionGrant')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LocationPermissionDialog;
